import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { BaseIndexPageComponent } from '../../../../../components/pages/base-index-page/base-index-page.component';
import BaseIndexPageConfig from '../../../../../components/pages/base-index-page/base-index-page.config';
import { DataTableColumnActions } from '../../../../../components/widgets/data-table/columns/data-table-column-actions/data-table-column-actions.config';
import { DataTableColumnText } from '../../../../../components/widgets/data-table/columns/data-table-column-text/data-table-column-text.config';
import ApiOptionsConfig from '../../../../../services/api/api-options.config';
import { QualityManagementService } from '../../../../../services/quality-management.service';
import { RouteService } from '../../../../../services/route.service';
import { MainLayoutService } from '../../../../../services/main-layout.service';
import { AccessControlService } from '../../../../../services/access-control.service';
import { RiskTimeFrame } from '../../../../../models/entities/risk';

@Component({
  selector: 'app-risk-time-frame-index-page',
  imports: [BaseIndexPageComponent],
  templateUrl: './risk-time-frame-index-page.component.html',
  styleUrl: './risk-time-frame-index-page.component.scss'
})
export class RiskTimeFrameIndexPageComponent implements OnInit, OnDestroy {

  baseIndexPageConfig: BaseIndexPageConfig;

  private activeProfileCode: string | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    protected qualityManagementService: QualityManagementService,
    private routeService: RouteService,
    private mainLayoutService: MainLayoutService,
    private accessControlService: AccessControlService
  ) {
    this.baseIndexPageConfig = {
      pageTitle: 'Risk Time Frame',
      cardTitle: 'Manage Risk Time Frames',
      dataTable: {
        data: [],
        searchModel: {
          code: null,
          name: null
        },
        pagination: null,
        columns: [
          new DataTableColumnText({
            attribute: 'code',
            header: 'Code',
            filter: 'text',
            value: (item: RiskTimeFrame) => item?.code ?? ''
          }),
          new DataTableColumnText({
            attribute: 'name',
            header: 'Name',
            filter: 'text',
            value: (item: RiskTimeFrame) => item?.name ?? ''
          }),
          new DataTableColumnActions({
            header: 'Actions',
            attribute: 'actions',
            style: { width: '120px' },
            buttons: [
              {
                icon: 'fa-eye',
                tooltip: 'View riskTimeFrame details',
                onClick: (item: RiskTimeFrame) => {
                  this.routeService.riskTimeFrameDetail(Number(item.riskTimeFrameId));
                }
              },
              {
                icon: 'fa-edit',
                tooltip: 'Edit riskTimeFrame',
                onClick: (item: RiskTimeFrame) => {
                  this.routeService.riskTimeFrameUpdate(Number(item.riskTimeFrameId));
                },
                hidden: () => !this.canModify()
              },
              {
                icon: 'fa-trash',
                tooltip: 'Delete riskTimeFrame',
                onClick: () => { },
                isDelete: true,
                hidden: () => !this.canDelete()
              }
            ]
          })
        ]
      },
      apiListService: (options: ApiOptionsConfig) => this.qualityManagementService.listRiskTimeFrames(options),
      responseListModelName: 'riskTimeFrames'
    };
  }

  ngOnInit(): void {
    this.mainLayoutService.activeProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe(profileCode => {
        this.activeProfileCode = profileCode;

        if (this.canModify()) {
          this.baseIndexPageConfig.addNewItemButton = {
            type: 'success',
            title: 'Add Time Frame',
            onClick: () => this.routeService.riskTimeFrameCreate()
          };
        } else {
          this.baseIndexPageConfig.addNewItemButton = undefined;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private canModify(): boolean {
    return this.accessControlService.hasFeatureActionAccess(
      this.activeProfileCode,
      'QUALITY_MANAGEMENT_SETTINGS_RISK_TIME_FRAMES',
      'modify'
    );
  }

  private canDelete(): boolean {
    return this.accessControlService.hasFeatureActionAccess(
      this.activeProfileCode,
      'QUALITY_MANAGEMENT_SETTINGS_RISK_TIME_FRAMES',
      'delete'
    );
  }
}
